import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react';
import { ITransaction } from '../interface/transaction';
import { ITransactionTypeFilterSelection } from '../interface/transactionTypes';

interface TransactionContextType {
  transaction: ITransaction | null;
  initialTransaction: ITransaction | null;
  updateTransaction: (
    transaction: ITransaction | null,
    shouldPrefillInitialTransaction?: boolean,
  ) => void;
  transactionTypeFilterSelection: ITransactionTypeFilterSelection | null;
  updateTransactionTypeFilterSelection: Dispatch<
    SetStateAction<ITransactionTypeFilterSelection | null>
  >;
}

const TransactionContext = createContext<TransactionContextType | undefined>(undefined);

export const TransactionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [transaction, setTransaction] = useState<ITransaction | null>(null);
  const [initialTransaction, setInitialTransaction] = useState<ITransaction | null>(null);
  const [transactionTypeFilterSelection, setTransactionTypeFilterSelection] =
    useState<ITransactionTypeFilterSelection | null>(null);

  const updateTransaction = useCallback(
    (data: ITransaction | null, shouldPrefillInitialTransaction?: boolean) => {
      if (shouldPrefillInitialTransaction) {
        setInitialTransaction(data);
      }
      // The picker page and the dropdown share the filter choice for as long as the form is open,
      // and the form is over once the transaction is reset.
      if (!data) {
        setTransactionTypeFilterSelection(null);
      }
      setTransaction(data);
    },
    [],
  );
  const transactionContextValue = useMemo(
    () => ({
      transaction,
      updateTransaction,
      initialTransaction,
      transactionTypeFilterSelection,
      updateTransactionTypeFilterSelection: setTransactionTypeFilterSelection,
    }),
    [transaction, updateTransaction, initialTransaction, transactionTypeFilterSelection],
  );
  return (
    <TransactionContext.Provider value={transactionContextValue}>
      {children}
    </TransactionContext.Provider>
  );
};

export const useTransaction = () => {
  const context = useContext(TransactionContext);
  if (!context) {
    throw new Error('useTransaction must be used within a TransactionProvider');
  }
  return context;
};
